import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../App';
import Navbar from '../components/Navbar';

const TIPO_COLORS = {
  login: '#4caf50',
  login_fallido: '#e94560',
  alerta_seguridad: '#ff5722',
  alerta_inactividad: '#ff9800',
  crear_entreno: '#2196f3',
  editar_entreno: '#ff9800',
  eliminar_entreno: '#e94560',
  aprobar_usuario: '#9c27b0',
};

function formatDate(iso) {
  if (!iso) return 'Nunca';
  const d = new Date(iso);
  const now = new Date();
  const diff = now - d;
  if (diff < 60000) return 'Hace un momento';
  if (diff < 3600000) return `Hace ${Math.floor(diff / 60000)} min`;
  if (diff < 86400000) return `Hace ${Math.floor(diff / 3600000)}h`;
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
}

function StatCard({ label, value, color }) {
  return (
    <div data-testid={`stat-${label.toLowerCase().replace(/\s/g, '-')}`} style={{
      background: 'linear-gradient(135deg, rgba(26,58,46,0.6) 0%, rgba(22,33,62,0.6) 100%)',
      padding: '20px', borderRadius: '12px', border: `1px solid ${color}33`,
      minWidth: '130px', flex: 1
    }}>
      <div style={{ fontSize: '12px', color, fontWeight: '600', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '8px' }}>
        {label}
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: '#e0e0e0' }}>{value}</div>
    </div>
  );
}

function AccessRow({ item }) {
  return (
    <div data-testid={`access-row-${item.id}`} style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px',
      background: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '4px'
    }}>
      <div style={{
        width: '32px', height: '32px', borderRadius: '50%', background: 'linear-gradient(135deg, #1a6030, #2a8040)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: '13px', fontWeight: '700', flexShrink: 0
      }}>
        {(item.user_nombre || 'U')[0].toUpperCase()}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', color: '#e0e0e0', fontWeight: '500' }}>{item.user_nombre || 'Usuario'}</div>
        <div style={{ fontSize: '11px', color: '#666' }}>IP: {item.ip || 'desconocida'}</div>
      </div>
      <div style={{ fontSize: '12px', color: '#888', flexShrink: 0 }}>{formatDate(item.fecha)}</div>
    </div>
  );
}

function ActivityRow({ item }) {
  const color = TIPO_COLORS[item.tipo] || '#888';
  return (
    <div data-testid={`activity-row-${item.id}`} style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
      background: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '6px',
      borderLeft: `3px solid ${color}`
    }}>
      <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', color: '#e0e0e0' }}>
          <strong style={{ color: '#d4af37' }}>{item.user_nombre || 'Usuario'}</strong>
          {' '}<span style={{ color: '#999' }}>{item.descripcion}</span>
        </div>
      </div>
      <div style={{ fontSize: '11px', color: '#666', flexShrink: 0, textAlign: 'right' }}>
        <div>{formatDate(item.fecha)}</div>
        {item.ip && <div style={{ color: '#555', fontSize: '10px' }}>{item.ip}</div>}
      </div>
    </div>
  );
}

function SecurityRow({ item }) {
  const isFailed = item.tipo === 'login_fallido';
  const isAlert = item.tipo === 'alerta_seguridad';
  const color = isAlert ? '#ff5722' : '#e94560';
  return (
    <div data-testid={`security-row-${item.id}`} style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
      background: isAlert ? 'rgba(255,87,34,0.08)' : 'rgba(233,69,96,0.05)',
      borderRadius: '8px', marginBottom: '6px', borderLeft: `3px solid ${color}`
    }}>
      <div style={{
        width: '28px', height: '28px', borderRadius: '50%', background: `${color}22`,
        display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '14px'
      }}>
        {isAlert ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2"><circle cx="12" cy="12" r="10"/><line x1="15" y1="9" x2="9" y2="15"/><line x1="9" y1="9" x2="15" y2="15"/></svg>
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '13px', color: '#e0e0e0' }}>
          {isAlert ? (
            <span style={{ color: '#ff5722', fontWeight: '600' }}>{item.descripcion}</span>
          ) : (
            <>
              <span style={{ color: '#e94560', fontWeight: '500' }}>{isFailed ? 'Login fallido' : item.tipo}</span>
              {' '}<span style={{ color: '#999' }}>{item.descripcion}</span>
            </>
          )}
        </div>
        <div style={{ fontSize: '11px', color: '#555', marginTop: '2px' }}>IP: {item.ip}</div>
      </div>
      <div style={{ fontSize: '11px', color: '#666', flexShrink: 0 }}>{formatDate(item.fecha)}</div>
    </div>
  );
}

function InactiveUserRow({ user }) {
  const severity = user.dias_inactivo >= 14 ? '#e94560' : user.dias_inactivo >= 7 ? '#ff9800' : '#ffeb3b';
  return (
    <div data-testid={`inactive-user-${user.user_id}`} style={{
      display: 'flex', alignItems: 'center', gap: '12px', padding: '12px 16px',
      background: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '6px',
      borderLeft: `3px solid ${severity}`
    }}>
      <div style={{
        width: '36px', height: '36px', borderRadius: '50%', background: 'linear-gradient(135deg, #333, #555)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#999', fontSize: '14px', fontWeight: '700', flexShrink: 0, overflow: 'hidden'
      }}>
        {user.foto_perfil ? (
          <img src={user.foto_perfil} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
        ) : (
          (user.nombre || 'U')[0].toUpperCase()
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: '14px', color: '#e0e0e0', fontWeight: '500' }}>{user.nombre}</div>
        <div style={{ fontSize: '11px', color: '#666' }}>{user.email}</div>
      </div>
      <div style={{ textAlign: 'right', flexShrink: 0 }}>
        <div style={{ fontSize: '14px', fontWeight: '700', color: severity }}>{user.dias_inactivo === 999 ? 'Nunca' : `${user.dias_inactivo}d`}</div>
        <div style={{ fontSize: '10px', color: '#555' }}>
          {user.ultimo_acceso ? formatDate(user.ultimo_acceso) : 'Sin accesos'}
        </div>
      </div>
    </div>
  );
}

export default function ActivityPage() {
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);
  const [accesses, setAccesses] = useState([]);
  const [security, setSecurity] = useState([]);
  const [inactive, setInactive] = useState([]);
  const [alertsCount, setAlertsCount] = useState({ security: 0, inactive: 0, total: 0 });
  const [downloadStats, setDownloadStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('accesses');
  const token = localStorage.getItem('token');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [statsRes, recentRes, accessesRes, securityRes, inactiveRes, alertsRes, dlRes] = await Promise.all([
        fetch(`${API}/admin/activity/stats`, { headers }),
        fetch(`${API}/admin/activity/recent`, { headers }),
        fetch(`${API}/admin/activity/accesses`, { headers }),
        fetch(`${API}/admin/activity/security`, { headers }),
        fetch(`${API}/admin/activity/inactive`, { headers }),
        fetch(`${API}/admin/activity/alerts/count`, { headers }),
        fetch(`${API}/descargar/stats`, { headers }),
      ]);
      if (statsRes.ok) setStats(await statsRes.json());
      if (recentRes.ok) setRecent(await recentRes.json());
      if (accessesRes.ok) setAccesses(await accessesRes.json());
      if (securityRes.ok) setSecurity(await securityRes.json());
      if (inactiveRes.ok) setInactive(await inactiveRes.json());
      if (alertsRes.ok) setAlertsCount(await alertsRes.json());
      if (dlRes.ok) setDownloadStats(await dlRes.json());
    } catch (e) {
      console.error('Error fetching activity:', e);
    }
    setLoading(false);
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const tabs = [
    { key: 'accesses', label: 'Ultimos Accesos' },
    { key: 'actions', label: 'Acciones Recientes' },
    { key: 'security', label: 'Seguridad', badge: alertsCount.security },
    { key: 'inactive', label: 'Inactivos', badge: alertsCount.inactive },
    { key: 'downloads', label: 'Descargas', badge: downloadStats?.today || 0 },
  ];

  return (
    <div data-testid="activity-page">
      <Navbar />
      <div style={{ maxWidth: '960px', margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '28px' }}>
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#d4af37" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
          </svg>
          <div style={{ flex: 1 }}>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#d4af37', margin: 0 }}>Actividad de la Aplicacion</h1>
            <p style={{ color: '#888', margin: '4px 0 0', fontSize: '14px' }}>Monitoriza accesos, acciones y alertas de seguridad</p>
          </div>
          <button data-testid="refresh-activity" onClick={fetchData} disabled={loading} style={{
            padding: '8px 12px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
            borderRadius: '8px', color: '#fff', cursor: 'pointer', fontSize: '13px', display: 'flex', alignItems: 'center', gap: '6px'
          }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10" />
            </svg>
            Actualizar
          </button>
        </div>

        {/* Stats */}
        {stats && (
          <div data-testid="stats-grid" style={{ display: 'flex', gap: '12px', marginBottom: '28px', flexWrap: 'wrap' }}>
            <StatCard label="Hoy" value={stats.users_today} color="#4caf50" />
            <StatCard label="Semana" value={stats.users_week} color="#2196f3" />
            <StatCard label="Accesos totales" value={stats.total_accesses} color="#ff9800" />
            <StatCard label="Acciones hoy" value={stats.actions_today} color="#9c27b0" />
          </div>
        )}

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2px', marginBottom: '16px', borderBottom: '1px solid rgba(255,255,255,0.1)', overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.key} data-testid={`tab-${t.key}`} onClick={() => setTab(t.key)} style={{
              padding: '10px 16px', background: tab === t.key ? 'rgba(212,175,55,0.15)' : 'transparent',
              border: 'none', borderBottom: tab === t.key ? '2px solid #d4af37' : '2px solid transparent',
              color: tab === t.key ? '#d4af37' : '#888', cursor: 'pointer', fontSize: '13px', fontWeight: '500',
              transition: 'all 0.2s', whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '6px'
            }}>
              {t.label}
              {t.badge > 0 && (
                <span style={{
                  background: t.key === 'security' ? '#e94560' : '#ff9800',
                  color: '#fff', fontSize: '10px', fontWeight: '700',
                  padding: '1px 6px', borderRadius: '10px', minWidth: '18px', textAlign: 'center'
                }}>{t.badge}</span>
              )}
            </button>
          ))}
        </div>

        {/* Content */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#888' }}>
            <div style={{ width: '32px', height: '32px', border: '3px solid #333', borderTop: '3px solid #d4af37', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
            Cargando...
          </div>
        ) : tab === 'accesses' ? (
          <div data-testid="accesses-list">
            {accesses.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                No hay accesos registrados
              </div>
            ) : accesses.map(a => <AccessRow key={a.id} item={a} />)}
          </div>
        ) : tab === 'actions' ? (
          <div data-testid="recent-list">
            {recent.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                No hay acciones recientes registradas
              </div>
            ) : recent.map(r => <ActivityRow key={r.id} item={r} />)}
          </div>
        ) : tab === 'security' ? (
          <div data-testid="security-list">
            <div style={{ marginBottom: '12px', padding: '12px 16px', background: 'rgba(233,69,96,0.08)', borderRadius: '8px', border: '1px solid rgba(233,69,96,0.2)' }}>
              <div style={{ fontSize: '13px', color: '#e94560', fontWeight: '600', marginBottom: '4px' }}>
                Intentos de login fallidos y alertas
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Se genera una alerta automatica cuando hay 3+ intentos fallidos del mismo email o IP en 1 hora. El entrenador recibe una notificacion.
              </div>
            </div>
            {security.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#4caf50', background: 'rgba(76,175,80,0.05)', borderRadius: '8px' }}>
                Sin incidentes de seguridad - Todo en orden
              </div>
            ) : security.map(s => <SecurityRow key={s.id} item={s} />)}
          </div>
        ) : tab === 'inactive' ? (
          <div data-testid="inactive-list">
            <div style={{ marginBottom: '12px', padding: '12px 16px', background: 'rgba(255,152,0,0.08)', borderRadius: '8px', border: '1px solid rgba(255,152,0,0.2)' }}>
              <div style={{ fontSize: '13px', color: '#ff9800', fontWeight: '600', marginBottom: '4px' }}>
                Usuarios con 5+ dias sin acceder
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Se genera una alerta diaria y notificacion cuando se detectan usuarios inactivos. Contactalos para mejorar la retencion.
              </div>
            </div>
            {inactive.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#4caf50', background: 'rgba(76,175,80,0.05)', borderRadius: '8px' }}>
                Todos los usuarios estan activos - Excelente retencion
              </div>
            ) : inactive.map(u => <InactiveUserRow key={u.user_id} user={u} />)}
          </div>
        ) : tab === 'downloads' ? (
          <div data-testid="downloads-list">
            {downloadStats && (
              <div style={{ display: 'flex', gap: '12px', marginBottom: '16px', flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: '100px', background: 'rgba(212,175,55,0.08)', border: '1px solid rgba(212,175,55,0.2)', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#d4af37' }}>{downloadStats.total}</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Total visitas</div>
                </div>
                <div style={{ flex: 1, minWidth: '100px', background: 'rgba(76,175,80,0.08)', border: '1px solid rgba(76,175,80,0.2)', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                  <div style={{ fontSize: '24px', fontWeight: '700', color: '#4caf50' }}>{downloadStats.today}</div>
                  <div style={{ fontSize: '11px', color: '#888' }}>Hoy</div>
                </div>
                {Object.entries(downloadStats.by_device || {}).map(([dev, count]) => (
                  <div key={dev} style={{ flex: 1, minWidth: '80px', background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: '12px', padding: '14px', textAlign: 'center' }}>
                    <div style={{ fontSize: '20px', fontWeight: '700', color: '#e0e0e0' }}>{count}</div>
                    <div style={{ fontSize: '11px', color: '#888' }}>{dev === 'ios' ? 'iPhone' : dev === 'android' ? 'Android' : 'PC'}</div>
                  </div>
                ))}
              </div>
            )}
            <div style={{ marginBottom: '12px', padding: '12px 16px', background: 'rgba(212,175,55,0.08)', borderRadius: '8px', border: '1px solid rgba(212,175,55,0.2)' }}>
              <div style={{ fontSize: '13px', color: '#d4af37', fontWeight: '600', marginBottom: '4px' }}>
                Pagina de descarga: /descargar
              </div>
              <div style={{ fontSize: '12px', color: '#888' }}>
                Comparte mallorcaklan.com/descargar o el QR para que nuevos clientes instalen la app.
              </div>
            </div>
            {(downloadStats?.recent || []).length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#555', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                Sin visitas aun
              </div>
            ) : (downloadStats.recent || []).map((v, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '10px 16px', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', marginBottom: '4px' }}>
                <div style={{ width: '28px', height: '28px', borderRadius: '50%', background: v.device === 'ios' ? 'rgba(0,122,255,0.15)' : v.device === 'android' ? 'rgba(76,175,80,0.15)' : 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', flexShrink: 0 }}>
                  {v.device === 'ios' ? 'iOS' : v.device === 'android' ? 'And' : 'PC'}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: '13px', color: '#e0e0e0' }}>Visita desde {v.device === 'ios' ? 'iPhone' : v.device === 'android' ? 'Android' : 'PC'}</div>
                  <div style={{ fontSize: '11px', color: '#555' }}>IP: {v.ip}</div>
                </div>
                <div style={{ fontSize: '11px', color: '#666', flexShrink: 0 }}>{formatDate(v.fecha)}</div>
              </div>
            ))}
          </div>
        ) : null}
      </div>

      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}
